import { z } from 'zod'
import './registry'
import { UlidSchema, ShortIdSchema, IsoDateSchema, MoneySchema, SignedMoneySchema } from './common'
import {
  TransactionSchema,
  ReturnSchema,
  ReturnReasonSchema,
  DiscountReasonSchema,
  VatTreatmentSchema,
} from './transactions'

/**
 * Admin-only reporting — sales-by-period and supplier-payout accounting
 * (Fase 4 of `docs/PROJECTVOORSTEL.md`). Both reports are computed from a
 * single shared date-range Query against `GSI5-TxnByDate` (see
 * `handlers/reports.ts`'s `queryTransactionsInRange`), never a separate
 * per-supplier index — see that handler's header comment for why `GSI3`
 * (provisioned but never written) is deliberately NOT used here.
 *
 * A reversed sale (`Transaction.status === 'reversed'`) is excluded from both
 * reports — it never counts toward sales totals or supplier payouts.
 *
 * Both the payouts report and the sales report claw back a return, always
 * dated by the RETURN's own `createdAt` — never the original sale's date —
 * so an already-run report for a past period is never silently rewritten;
 * the clawback simply lands in whichever period the return itself falls
 * into. Payouts: a `Return` with a non-null `supplierId` snapshot (see
 * `schemas/transactions.ts`) produces a NEGATIVE `PayoutsReportLine`
 * (`kind: 'return'`). Sales: EVERY return (any `Return`, supplier-linked or
 * not) reduces `SalesReportPeriod.returnsEur`/`totalEur` by its
 * `refundedEur` — the actual cash that left the till, which is a different
 * (and for revenue purposes correct) basis than payouts' gross-of-basket-
 * discount `lineTotalEur` (see `handlers/reports.ts`'s payout comment for
 * why those two deliberately differ). Both can go negative in a period with
 * more returns than sales, hence `SignedMoneySchema` on those totals.
 */

/** A calendar date the admin picks from a date input — NOT a full timestamp. */
export const ReportDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
  .openapi('ReportDate', { example: '2026-01-31' })

export const ReportGroupBySchema = z.enum(['day', 'week', 'month']).openapi('ReportGroupBy')
export type ReportGroupBy = z.infer<typeof ReportGroupBySchema>

/**
 * Sales-by-period report. `groupBy: 'week'` buckets by the Monday-anchored
 * calendar date of that ISO week (e.g. "2026-01-05"), not an ISO week number
 * ("2026-W02") — simpler to compute (no year-boundary edge cases) and more
 * readable in the UI.
 */
export const SalesReportQuerySchema = z
  .object({
    from: ReportDateSchema,
    to: ReportDateSchema,
    groupBy: ReportGroupBySchema,
  })
  .refine(v => v.from <= v.to, { message: '"from" must not be after "to"', path: ['from'] })
  .openapi('SalesReportQuery')
export type SalesReportQuery = z.infer<typeof SalesReportQuerySchema>

export const SalesReportPeriodSchema = z
  .object({
    period: z.string(),
    /** Sum of sold transactions' totalEur for this period — unaffected by returns. */
    grossEur: MoneySchema,
    /**
     * Negative sum of `Return.refundedEur` for returns dated (by their OWN
     * createdAt, never the original sale's) into this period. A return can
     * claw back a sale from an earlier, already-reported period — that period
     * is never rewritten; the clawback lands here instead. Always ≤ 0.
     */
    returnsEur: SignedMoneySchema,
    /** grossEur + returnsEur — can go negative in a period with more returns than sales. */
    totalEur: SignedMoneySchema,
    /** Count of SALES in this period — a return is not a sale, so it's not counted here. */
    transactionCount: z.number().int().nonnegative(),
  })
  .openapi('SalesReportPeriod')

export const SalesReportResponseSchema = z
  .object({
    periods: z.array(SalesReportPeriodSchema),
    summary: z.object({
      grossEur: MoneySchema,
      returnsEur: SignedMoneySchema,
      totalEur: SignedMoneySchema,
      transactionCount: z.number().int().nonnegative(),
    }),
  })
  .openapi('SalesReportResponse')
export type SalesReportResponse = z.infer<typeof SalesReportResponseSchema>

export const PayoutsReportQuerySchema = z
  .object({
    from: ReportDateSchema,
    to: ReportDateSchema,
    /** Narrow to one supplier; omitted returns every supplier with sales in range. */
    supplierId: UlidSchema.optional(),
  })
  .refine(v => v.from <= v.to, { message: '"from" must not be after "to"', path: ['from'] })
  .openapi('PayoutsReportQuery')
export type PayoutsReportQuery = z.infer<typeof PayoutsReportQuerySchema>

export const PayoutsReportLineSchema = z
  .object({
    /** 'return' is a negative clawback line for a since-returned sale — see the header comment. */
    kind: z.enum(['sale', 'return']),
    recordId: UlidSchema,
    shortId: ShortIdSchema,
    description: z.string(),
    /** The sale's createdAt for `kind: 'sale'`; the RETURN's createdAt for `kind: 'return'`. */
    soldAt: IsoDateSchema,
    /**
     * Unit price before any discount — lets the payout view show what a discount
     * was given against. Null for a `kind: 'return'` line: a clawback has no
     * distinct "unit price before discount" concept, only the amount reversed.
     */
    unitPriceEur: MoneySchema.nullable(),
    /** The (positive) sale amount — for a return line, the ORIGINAL sale's line total being clawed back. */
    lineTotalEur: MoneySchema,
    /** True when this line's own price was discounted (unitPriceEur > lineTotalEur). Always false for a return line. */
    hasLineDiscount: z.boolean(),
    /** Why the line discount was given, if recorded. Null for a return line. */
    lineDiscountReason: DiscountReasonSchema.nullable(),
    /** Free-text detail; set only when `lineDiscountReason === 'other'`. */
    lineDiscountReasonNote: z.string().min(1).max(300).nullable(),
    /**
     * True when the SALE this line belongs to also carried a whole-basket
     * discount. Per the confirmed payout policy, a basket discount never
     * reduces `owedEur` (see handlers/reports.ts) — this flag is for display
     * only, so the payout view can show *why* a discounted sale still pays
     * the supplier in full. Always false for a return line.
     */
    hasBasketDiscount: z.boolean(),
    /** Why the basket discount was given, if recorded. Null for a return line. */
    basketDiscountReason: DiscountReasonSchema.nullable(),
    /** Free-text detail; set only when `basketDiscountReason === 'other'`. */
    basketDiscountReasonNote: z.string().min(1).max(300).nullable(),
    /** Revenue-share % locked at sale time (0-100) — never the supplier's current config. */
    sharePercent: z.number().min(0).max(100),
    /**
     * VAT treatment of the sold line: `standard` (new stock, price includes
     * VAT) pays the supplier's share on the ex-VAT amount; `margin`
     * (second-hand, margeregeling) on the full line total. Null only for a
     * `kind: 'return'` line whose Return predates the vatTreatment snapshot —
     * such a clawback uses the gross basis its sale was credited under.
     */
    vatTreatment: VatTreatmentSchema.nullable(),
    /**
     * The amount `sharePercent` was applied to: `lineTotalEur` net of VAT for
     * a standard-rated line, `lineTotalEur` itself otherwise. Always positive,
     * also for a return line (its clawback direction lives in `owedEur`).
     */
    payoutBasisEur: MoneySchema,
    /** Negative for a `kind: 'return'` line (a clawback against this supplier's payout). */
    owedEur: SignedMoneySchema,
    /** Set only for `kind: 'return'` — why the copy came back. */
    returnReason: ReturnReasonSchema.nullable(),
  })
  .openapi('PayoutsReportLine')
export type PayoutsReportLine = z.infer<typeof PayoutsReportLineSchema>

export const PayoutsReportSupplierSchema = z
  .object({
    supplierId: UlidSchema,
    supplierName: z.string(),
    lines: z.array(PayoutsReportLineSchema),
    /** Can be negative when this supplier's returns in-range outweigh their sales in-range. */
    totalOwedEur: SignedMoneySchema,
  })
  .openapi('PayoutsReportSupplier')

export const PayoutsReportResponseSchema = z
  .object({
    suppliers: z.array(PayoutsReportSupplierSchema),
    grandTotalOwedEur: SignedMoneySchema,
  })
  .openapi('PayoutsReportResponse')
export type PayoutsReportResponse = z.infer<typeof PayoutsReportResponseSchema>

/**
 * Transaction browser — unlike sales/payouts, this deliberately INCLUDES
 * reversed transactions (an admin needs to see "was this reversed, by whom,
 * why", not just revenue) AND returns (a return is a distinct, later event
 * against a sale — never a change to the original transaction itself; see
 * `schemas/transactions.ts`'s `ReturnSchema` header comment). `status`
 * narrows to one SALE bucket; a narrowed status hides returns entirely
 * (they have no completed/reversed concept of their own) — only the
 * default `all` shows both sales and returns together, newest first.
 */
export const TransactionsReportStatusSchema = z
  .enum(['all', 'completed', 'reversed'])
  .openapi('TransactionsReportStatus')
export type TransactionsReportStatus = z.infer<typeof TransactionsReportStatusSchema>

export const TransactionsReportQuerySchema = z
  .object({
    from: ReportDateSchema,
    to: ReportDateSchema,
    status: TransactionsReportStatusSchema.default('all'),
  })
  .refine(v => v.from <= v.to, { message: '"from" must not be after "to"', path: ['from'] })
  .openapi('TransactionsReportQuery')
export type TransactionsReportQuery = z.infer<typeof TransactionsReportQuerySchema>

/**
 * A sale as shown in the report, with `cashierId`/`reversedBy` (raw Cognito
 * subs) resolved to a display name where possible — see `schemas/staff.ts`.
 * `null` means neither an admin-set name nor a cached email was ever found
 * for that sub (the UI falls back to the raw id).
 */
export const TransactionsReportSaleItemSchema = TransactionSchema.extend({
  kind: z.literal('sale'),
  cashierDisplayName: z.string().nullable(),
  reversedByDisplayName: z.string().nullable(),
}).openapi('TransactionsReportSaleItem')
export type TransactionsReportSaleItem = z.infer<typeof TransactionsReportSaleItemSchema>

/** A return as shown in the report, interleaved with sales — see the header comment. */
export const TransactionsReportReturnItemSchema = ReturnSchema.extend({
  kind: z.literal('return'),
  cashierDisplayName: z.string().nullable(),
}).openapi('TransactionsReportReturnItem')
export type TransactionsReportReturnItem = z.infer<typeof TransactionsReportReturnItemSchema>

export const TransactionsReportItemSchema = z
  .discriminatedUnion('kind', [
    TransactionsReportSaleItemSchema,
    TransactionsReportReturnItemSchema,
  ])
  .openapi('TransactionsReportItem')
export type TransactionsReportItem = z.infer<typeof TransactionsReportItemSchema>

export const TransactionsReportResponseSchema = z
  .object({
    transactions: z.array(TransactionsReportItemSchema),
  })
  .openapi('TransactionsReportResponse')
export type TransactionsReportResponse = z.infer<typeof TransactionsReportResponseSchema>
